import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export function useTrialStatus() {
  const { user } = useAuth();
  const [trialStatus, setTrialStatus] = useState<{
    isTrial: boolean;
    endsAt: string | null;
    isExpired: boolean;
    loading: boolean;
  }>({
    isTrial: false,
    endsAt: null,
    isExpired: false,
    loading: true,
  });

  const fetchTrialStatus = useCallback(async () => {
    if (!user) {
      setTrialStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      const { data: subscriptions, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_trial", true)
        .eq("status", "active")
        .limit(1);

      if (error) {
        console.error("Error fetching trial status:", error);
        setTrialStatus(prev => ({ ...prev, loading: false }));
        return;
      }

      if (subscriptions && subscriptions.length > 0) {
        const subscription = subscriptions[0];
        const now = new Date();
        const trialEndDate = new Date(subscription.trial_ends_at);
        setTrialStatus({
          isTrial: true,
          endsAt: subscription.trial_ends_at,
          isExpired: now > trialEndDate,
          loading: false,
        });
      } else {
        setTrialStatus({
          isTrial: false,
          endsAt: null,
          isExpired: false,
          loading: false,
        });
      }
    } catch (err) {
      console.error("Trial status error:", err);
      setTrialStatus(prev => ({ ...prev, loading: false }));
    }
  }, [user]);

  useEffect(() => {
    fetchTrialStatus();
  }, [fetchTrialStatus]);

  return trialStatus;
}
